import { useEffect, useMemo, useState } from 'react';
import { useCalendarSharingStore } from '../../../lib/stores/calendarSharingStore';
import { parseTime } from '../../../lib/calendar/team/availability';
import { useEnsureSharing } from '../../../lib/calendar/team/useEnsureSharing';
import { useTeamColumns } from '../../../lib/calendar/team/useTeamColumns';
import { gridRange } from '../../../lib/calendar/team/workHours';
import { TeamColumn, TeamColumnHeader } from './TeamColumn';
import { TeamToolbar } from './TeamToolbar';

export interface TeamSlot { userId: string; start: Date; end: Date }

interface Props {
  /** giorno di partenza (oggi se assente) */
  initialDay?: Date;
  /** versione stretta per il pannello laterale */
  compact?: boolean;
  /** clic su un buco libero; senza, la vista e' in sola lettura */
  onPickSlot?: (slot: TeamSlot) => void;
  /** clic su un'indisponibilità */
  onOpenUnavailability?: (id: string) => void;
  defaultDuration?: number;
}

/**
 * Giornata con una colonna per persona: impegni, fuori orario, buchi liberi nell'orario di lavoro
 * di ciascuno (di base lunedi-venerdi 9-18, si cambia in Impostazioni › Calendario › Orari). Il filtro "Liberi alle" attenua chi e' occupato a quell'ora.
 */
export function TeamAvailabilityView({ initialDay, compact, onPickSlot, onOpenUnavailability, defaultDuration = 60 }: Props) {
  useEnsureSharing();
  const [day, setDay] = useState(() => initialDay ?? new Date());
  // Reparto e persone si ricordano per utente (calendar_preferences.team): l'operatrice sceglie "Consulenti" una volta sola
  const savedTeam = useCalendarSharingStore(s => s.prefs.team);
  const setPrefs = useCalendarSharingStore(s => s.setPrefs);
  const [departmentId, setDepartmentIdState] = useState(() => savedTeam?.department_id || 'all');
  const [picked, setPickedState] = useState<string[] | null>(() => savedTeam?.people ?? null);
  const setDepartmentId = (id: string) => { setDepartmentIdState(id); setPickedState(null); void setPrefs({ team: { department_id: id, people: null } }); };
  // Il reparto si legge dalle preferenze appena salvate: la barra chiama onDepartment e subito dopo onPicked(null)
  const setPicked = (ids: string[] | null) => {
    setPickedState(ids);
    const dept = useCalendarSharingStore.getState().prefs.team?.department_id ?? departmentId;
    void setPrefs({ team: { department_id: dept, people: ids } });
  };
  // Preferenze caricate dopo l'apertura della vista: si allinea una volta
  useEffect(() => {
    if (savedTeam?.department_id) setDepartmentIdState(savedTeam.department_id);
    if (savedTeam && savedTeam.people !== undefined) setPickedState(savedTeam.people ?? null);
  }, [savedTeam?.department_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [freeAtText, setFreeAtText] = useState('');
  const [duration, setDuration] = useState(defaultDuration);
  const { seen, inDepartment, columns, departments } = useTeamColumns(day, departmentId, picked);
  // Griglia uguale per tutti: dalle 8 alle 19, allargata per chi lavora prima o dopo
  const range = useMemo(() => gridRange(columns.map(c => c.work)), [columns]);
  const hours = Array.from({ length: (range.end - range.start) / 60 }, (_, i) => range.start / 60 + i);

  const parsed = freeAtText ? parseTime(freeAtText) : NaN;
  const freeAt = Number.isNaN(parsed) ? null : parsed;
  const hourHeight = compact ? 36 : 48;
  const colWidth = compact ? 128 : 168;
  const template = `44px repeat(${Math.max(columns.length, 1)}, minmax(${colWidth}px, 1fr))`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-[#191919]" data-testid="team-availability">
      <TeamToolbar
        day={day} onDay={setDay} departments={departments} seen={seen}
        departmentId={departmentId} onDepartment={setDepartmentId}
        inDepartment={inDepartment} picked={picked} onPicked={setPicked}
        freeAt={freeAtText} onFreeAt={setFreeAtText} duration={duration} onDuration={setDuration} compact={compact}
      />
      {columns.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-[#787774]">
          Nessun calendario da mostrare. Scegli un altro reparto o altre persone.
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="grid" style={{ gridTemplateColumns: template, minWidth: 'max-content' }}>
            <div className="sticky top-0 z-10 col-span-full grid border-b border-[rgba(55,53,47,0.09)] bg-white dark:border-white/10 dark:bg-[#191919]" style={{ gridTemplateColumns: template }}>
              <div />
              {columns.map(c => <TeamColumnHeader key={c.id} col={c} freeAt={freeAt} duration={duration} compact={compact} />)}
            </div>
            <div className="relative">
              {hours.map(hh => (
                <div key={hh} className="pr-1 text-right text-[10px] text-[#787774]" style={{ height: hourHeight }}>{hh}:00</div>
              ))}
            </div>
            {columns.map(c => (
              <TeamColumn key={c.id} col={c} day={day} hourHeight={hourHeight} freeAt={freeAt} duration={duration} compact={compact} range={range} onPick={onPickSlot} onOpenUnavailability={onOpenUnavailability} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
